/**
 * Recap モーダルコンポーネント。
 *
 * BilateralTradeRecap の全詳細を表示する read-only モーダル。
 * 設計準拠:
 *   - closeOnInteractOutside=false (= 共通 Modal 規約 AC-014)
 *   - loading/error/data を内部で出し分ける (= レビュー指摘排他バグ修正)
 *   - バッジセクション (売買 / 約定経路 / 受渡区分) + 2 列詳細テーブル
 *   - 電力量料金行は data-testid="recap-total" で視覚的に強調
 *   - 成約ID (DealId) は内部管理用の識別子のため表示しない (spec
 *     bilateral-trade-recap.md「Recap モーダルに成約IDを表示するか」= B、 #6380)
 */
import type { BilateralTradeRecap } from '@ebalance/proto/bffuser/v1/supply_bilateral_trade_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Flex,
  HStack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'

import { Modal } from '~/components/common/Modal'
import { RegionLoading } from '~/components/common/RegionLoading'
import { TradeSideLabel } from '~/components/common/TradeSideLabel'
// 利用率行・通告タイミング行は本画面のスコープ外 (通告変更管理 P3 へ移管。
// spec bilateral-trade-recap.md AC-SBR-008〜010 欠番)。
import {
  AreaCodeLabel,
  DeliveryTypeBadge,
  ExecutionRouteBadge,
  StatusBadge,
  TimeZoneTypeLabel,
} from '~/pages/SupplyBilateral/components/Badges'
import {
  formatCounterparty,
  formatDeliveryPeriod,
  formatEnergyKwh,
  formatExecutionDate,
  formatQuantityMw,
  formatSupplyHours,
  formatTotalAmount,
  formatUnitPrice,
} from '~/pages/SupplyBilateral/models/recapFormatters'

type Props = {
  isOpen: boolean
  onClose: () => void
  /** Recap 詳細。 fetch 中は undefined。 */
  recap?: BilateralTradeRecap | undefined
  /** データ取得中 */
  isLoading?: boolean
  /** データ取得エラー */
  isError?: boolean
}

/** 代替表示文字 (= 未設定値の placeholder)。 recapFormatters と揃える。 */
const FALLBACK = '—'

/** 値が「—」 でなければ単位を後置する (= 単位はラベルでなく値側に表示する)。 */
function withUnit(value: string, unit: string): string {
  return value === FALLBACK ? value : `${value} ${unit}`
}

type DetailRowProps = {
  label: string
  /** 値。 文字列のほか、 ステータス等のバッジ要素も受ける。 */
  value: React.ReactNode
  isTotal?: boolean
}

/**
 * 2 列詳細テーブルの 1 行 (ラベル列 + 値列)。
 * ラベル列は固定幅 + ヘッダ色背景、 値はラベルの右に左寄せ
 * (design/プロトタイプ準拠: 値は右寄せにしない)。
 */
const DetailRow = ({
  label,
  value,
  isTotal = false,
}: DetailRowProps): React.ReactElement => {
  const rowContent = (
    <Flex borderColor="Border.lowEmphasis" borderBottomWidth={1}>
      {/* ラベル列: 固定幅 + eclair Surface.tableHeader (#e7eef2) 背景。
          TH 相当のセルのため文字色は design 例外で Text.midEmphasis
          (design/CLAUDE.md 「TH は mid-emphasis」)。 */}
      <Box
        flexShrink={0}
        width="160px"
        background="Surface.tableHeader"
        paddingX={3}
        paddingY={1.5}
      >
        {/* 8080 `.bi-recap-table th` = md (14px) / mid-emphasis。 手入力取引閲覧モーダルの
            行見出しと同じ role (tableHeader) にそろえる。 */}
        <Text
          textStyle="tableHeader"
          color="Text.midEmphasis"
          fontWeight={isTotal ? 'bold' : undefined}
        >
          {label}
        </Text>
      </Box>
      {/* 値列: 左寄せ。 isTotal (電力量料金の合計行) の淡背景は情報系の強調
          (= カテゴリ識別ではなく「この行が重要」 という情報表示) のため
          Surface.infoLow に DS token 化 (B-3、2026-07-05 ユーザー判断)。 */}
      <Box
        flex={1}
        background={isTotal ? 'Surface.infoLow' : undefined}
        paddingX={3}
        paddingY={1.5}
      >
        {typeof value === 'string' ? (
          <Text textStyle="tableCell" fontWeight={isTotal ? 'bold' : undefined}>
            {value}
          </Text>
        ) : (
          value
        )}
      </Box>
    </Flex>
  )

  if (isTotal) {
    return <div data-testid="recap-total">{rowContent}</div>
  }
  return rowContent
}

/**
 * Recap モーダル (Feature B)。
 *
 * loading / error / data を内部で出し分け、 page 側の 3 分岐を 1 つに統合する
 * (= レビュー指摘排他バグ修正)。
 *
 * props:
 *   - isOpen: モーダルの開閉状態
 *   - onClose: 閉じるコールバック
 *   - recap: BilateralTradeRecap (getBilateralTrade の戻り値)。 fetch 中/エラー時は undefined
 *   - isLoading: fetch 中フラグ
 *   - isError: fetch エラーフラグ
 */
export const RecapModal = ({
  isOpen,
  onClose,
  recap,
  isLoading = false,
  isError = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  // footer の「閉じる」ボタン (プロトタイプ準拠で右下に配置)。 modal footer のボタンは
  // DS 既定サイズ (md)。 手入力取引閲覧モーダルと同じ (2026-09-16 デザイナー指摘
  // 「modal のボタンも DS 違反」)。
  const footer = (
    <TertiaryButton onClick={onClose}>
      {t('page:supplyBilateral.recap.close')}
    </TertiaryButton>
  )

  return (
    <Modal
      footer={footer}
      isOpen={isOpen}
      onClose={onClose}
      // 8080 は Recap を詳細モーダル `#bi-detail-modal` (`ecl-modal--xl`) で開く
      size="xl"
      title={t('page:supplyBilateral.recap.title')}
    >
      {/* loading 中: 影 + スピナーで本文の領域を覆う (フッターは覆わない) */}
      {isLoading && <RegionLoading isLoading />}

      {/* エラー時: エラーメッセージ表示 */}
      {!isLoading && isError && (
        <Alert
          description={t(
            'page:supplyBilateral.recap.error.fetchFailedDescription',
          )}
          status="error"
          title={t('page:supplyBilateral.recap.error.fetchFailedTitle')}
        />
      )}

      {/* data あり: 詳細表示 */}
      {!isLoading && !isError && recap !== undefined && (
        <RecapDetail recap={recap} />
      )}
    </Modal>
  )
}

type RecapDetailProps = {
  recap: BilateralTradeRecap
}

/** Recap 詳細本体 (= バッジセクション + 2 列詳細テーブル)。 */
const RecapDetail = ({ recap }: RecapDetailProps): React.ReactElement => {
  const { t } = useTranslation()

  // 経路 / ステータス / 受渡区分のバッジは一覧行と同じ共有 component
  // (`ExecutionRouteBadge` / `StatusBadge` / `DeliveryTypeBadge`) に委譲する。
  // 以前はこの file がローカルの `KindBadge` + chakra パレットの色マップを持ち、
  // **同じ値が一覧と Recap で別色**になっていた (2026-09-07 Des 指摘)。
  // 種別 → semantic status の写像は共有 component 側が SoT。

  // 表示用値 (AreaCodeLabel / TimeZoneTypeLabel は component で i18n 解決するため使わない)
  const executionDateStr =
    recap.executionDate !== undefined
      ? formatExecutionDate(recap.executionDate)
      : '—'

  const deliveryPeriodStr =
    recap.deliveryPeriodFrom !== undefined &&
    recap.deliveryPeriodTo !== undefined
      ? formatDeliveryPeriod(recap.deliveryPeriodFrom, recap.deliveryPeriodTo)
      : '—'

  // オプションラベル / 利用率 (実績・範囲) / 通告タイミングの派生値は本画面の
  // スコープ外 (通告変更管理 P3 へ移管。 spec bilateral-trade-recap.md
  // AC-SBR-008〜010 欠番)。

  return (
    <>
      {/* バッジセクション: 売買 / 経路 / 受渡 を前置ラベル付きで表示。
          ステータスは詳細テーブルの行に移動 (プロトタイプ準拠)。 ラベルは 8080
          `.bi-recap-badges > span` = md / high-emphasis。 末尾にコロンを付けない
          (conventions.md「見出し・ラベルの末尾には何も付けない」)。 */}
      <HStack flexWrap="wrap" gap={4} paddingX={4} paddingY={3}>
        <HStack gap={1}>
          <Text textStyle="body" color="Text.highEmphasis">
            {t('page:supplyBilateral.recap.badge.buySell')}
          </Text>
          <TradeSideLabel side={recap.buySell} />
        </HStack>
        <HStack gap={1}>
          <Text textStyle="body" color="Text.highEmphasis">
            {t('page:supplyBilateral.recap.badge.route')}
          </Text>
          {/* 約定経路も一覧行と同じ `ExecutionRouteBadge` (= DS `Badge`) を使う
              (2026-09-07 Des 指摘)。 以前はこの箇所だけローカルの `KindBadge` に
              chakra パレット (blue / green / orange) を直接渡す 7 色マップで、
              同じ経路が一覧と Recap で別色になっていた。

              design (`supply/bilateral.html:681-694`) の色分けは **3 段**:
              eSquare Live = `badge-ok` / enechain = `badge-info` / 手入力・
              ベースロード市場・先渡し市場・常時BU・その他 = `badge-neutral`。
              共有 component の `EXECUTION_ROUTE_BADGE_STATUS` が既にこの写像を
              持っているので、 写経せず委譲する。 */}
          <ExecutionRouteBadge route={recap.executionRoute} />
        </HStack>
        <HStack gap={1}>
          <Text textStyle="body" color="Text.highEmphasis">
            {t('page:supplyBilateral.recap.badge.delivery')}
          </Text>
          {/* 受渡区分は一覧行・手入力取引閲覧モーダルと同じ `DeliveryTypeBadge`
              (= DS `Badge`) を使い、 同じ区分が画面ごとに違う色にならないようにする (issue #7278 論点1)。
              色分けは **現物 (BG/JEPX) = neutral / 先物 (EEX・TOCOM・ICE・スワップ) = info の
              2 グループ** (UI 辞書 bilateral-trade.md 2e)。 6 種それぞれに別色は割り当てない。
              種別名はバッジの文字で読める。 */}
          <DeliveryTypeBadge deliveryType={recap.deliveryType} />
        </HStack>
      </HStack>

      {/* 2 列詳細テーブル */}
      <Box borderColor="Border.lowEmphasis" borderTopWidth={1}>
        <DetailRow
          label={t('page:supplyBilateral.recap.label.publicTradeId')}
          value={recap.publicTradeId}
        />
        {/* ステータスも一覧行と同じ `StatusBadge` (= DS `Badge`) に委譲する
            (2026-09-07 Des 指摘)。 種別解決 `resolveStatusKind` は元から共通 SoT
            だったが、 **色とラベルだけ**この箇所でローカルに持ち直していた。 */}
        <DetailRow
          label={t('page:supplyBilateral.recap.label.status')}
          value={<StatusBadge status={recap.status} />}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.executionDate')}
          value={executionDateStr}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.partner')}
          value={formatCounterparty(
            recap.partnerName,
            recap.partnerSubmissionEntityCode,
          )}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.area')}
          value={<AreaCodeLabel areaCode={recap.areaCode} />}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.deliveryPeriod')}
          value={deliveryPeriodStr}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.timeZoneType')}
          value={<TimeZoneTypeLabel timeZoneType={recap.timeZoneType} />}
        />
        {/* オプション行 / 通告タイミング行 / 利用率行は本画面のスコープ外
            (PdM 確定「オプションに関する表記はカット。 通告変更管理が入る
            まではいらない」)。 spec `bilateral-trade-recap.md` でも AC-SBR-008〜010 は
            「オプション設定の有無・通告タイミング・実績利用率の表示 AC は通告変更管理
            画面へ移管 (P3)」で欠番。 */}
        <DetailRow
          label={t('page:supplyBilateral.recap.label.volumeMw')}
          value={withUnit(formatQuantityMw(recap.volumeMw?.value), 'MW')}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.supplyHours')}
          value={withUnit(formatSupplyHours(recap.supplyHours?.value), 'h')}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.energyKwh')}
          value={withUnit(formatEnergyKwh(recap.energyKwh?.value), 'kWh')}
        />
        <DetailRow
          label={t('page:supplyBilateral.recap.label.unitPrice')}
          value={withUnit(
            formatUnitPrice(recap.unitPriceYenPerKwh?.value),
            '円/kWh',
          )}
        />
        {/* 電力量料金: 強調表示 (recap-total)。 ¥ 前置 (プロトタイプ準拠)。 */}
        <DetailRow
          isTotal
          label={t('page:supplyBilateral.recap.label.totalAmount')}
          value={(() => {
            const amount = formatTotalAmount(recap.totalAmountYen?.value)
            return amount === FALLBACK ? amount : `¥${amount}`
          })()}
        />
      </Box>
    </>
  )
}
